import { ChartPanel } from "@/components/analytics/chart-panel";
import { KpiReadout } from "@/components/ui/kpi-readout";
import type { ForecastDiagnostics } from "./queries";

interface ForecastDiagnosticsPanelProps {
  data: ForecastDiagnostics;
}

export function ForecastDiagnosticsPanel({ data }: ForecastDiagnosticsPanelProps) {
  // Legacy revision without per_group data
  if (!data.per_group || data.per_group.length === 0) {
    return (
      <ChartPanel title="Forecast Diagnostics">
        <div className="rounded-md border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-900/20">
          <p className="text-xs text-amber-800 dark:text-amber-200">
            This revision was trained before per-group diagnostics were available. Retrain the model
            for detailed diagnostics.
          </p>
        </div>
      </ChartPanel>
    );
  }

  return (
    <div className="space-y-6">
      {/* KPI Strip */}
      <div className="grid grid-cols-3 gap-3">
        <KpiReadout label="Strategy" value={data.strategy ?? "—"} />
        <KpiReadout label="Validation Windows" value={data.num_val_windows?.toString() ?? "—"} />
        <KpiReadout label="Train Rows" value={data.train_rows?.toLocaleString() ?? "—"} />
      </div>

      {/* Per-Group Scores Table */}
      <ChartPanel title="Per-Group Scores">
        <div className="max-h-[400px] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-surface">
              <tr className="border-b border-line">
                <th className="px-3 py-2 text-left font-mono text-[10px] font-semibold uppercase tracking-wider text-navy-300">
                  Group
                </th>
                <th className="px-3 py-2 text-right font-mono text-[10px] font-semibold uppercase tracking-wider text-navy-300">
                  Score
                </th>
                <th className="px-3 py-2 text-right font-mono text-[10px] font-semibold uppercase tracking-wider text-navy-300">
                  N Obs
                </th>
                <th className="px-3 py-2 text-center font-mono text-[10px] font-semibold uppercase tracking-wider text-navy-300">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {data.per_group.map((group) => (
                <tr key={group.group_id} className="border-b border-line/50 hover:bg-line/20">
                  <td className="px-3 py-2.5 font-mono text-xs text-navy-700 dark:text-zinc-100">
                    {group.group_id}
                  </td>
                  <td className="px-3 py-2.5 text-right font-mono tabular-nums">
                    {group.metric_score !== null ? group.metric_score.toFixed(3) : "—"}
                  </td>
                  <td className="px-3 py-2.5 text-right font-mono tabular-nums">
                    {group.n_obs.toLocaleString()}
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    {group.insufficient_history ? (
                      <span className="inline-flex rounded bg-amber-100 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                        Insufficient
                      </span>
                    ) : (
                      <span className="text-[10px] text-muted">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Truncation footer */}
        {data.truncated_at && (
          <div className="mt-2 text-center font-mono text-[10px] text-muted">
            Results truncated at {data.truncated_at.toLocaleString()} groups
          </div>
        )}
      </ChartPanel>
    </div>
  );
}
